/**
 * @file components/chat/HitlActionCard.tsx
 * Validation HITL directement dans le fil.
 * Alerte orange + deux gros boutons Approuver / Rejeter. Une fois la décision
 * prise, la carte se fige : "✓ Approuvé par vous à 14h30".
 */
import React from 'react';
import { AlertTriangle, Check, X } from 'lucide-react';
import type { HitlMessage } from './types.ts';

export default function HitlActionCard({
  message,
  busy = false,
  onDecision,
}: {
  message: HitlMessage;
  busy?: boolean;
  onDecision?: (dossierId: string, approved: boolean) => void;
}): React.ReactElement {
  const resolved = message.resolution;

  return (
    <div className="flex items-end gap-2">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600">
        <AlertTriangle size={15} />
      </div>
      <div className="w-[85%] max-w-lg rounded-2xl rounded-bl-sm border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950 shadow-sm dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
        <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
          Validation requise
        </p>
        <p className="mt-1 text-sm leading-relaxed">{message.text}</p>

        {!resolved ? (
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => onDecision?.(message.dossierId, true)}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-green-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:opacity-50"
            >
              <Check size={16} />
              Approuver
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => onDecision?.(message.dossierId, false)}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
            >
              <X size={16} />
              Rejeter
            </button>
          </div>
        ) : (
          <div
            className={`mt-3 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium ${
              resolved.decision === 'approved'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
            }`}
          >
            {resolved.decision === 'approved' ? <Check size={15} /> : <X size={15} />}
            {resolved.decision === 'approved' ? 'Approuvé' : 'Rejeté'} par vous à {resolved.at}
          </div>
        )}
      </div>
    </div>
  );
}
